import { get } from 'utils/http';
import { BASE_URL } from 'constants/env';
import {
    convertPSMediaToCMSMedia,
    isCMSMedia,
    isCoverImageSchema,
} from 'utils/media-utils';
import { useEffect, useState, useMemo } from 'react';
import { parse } from 'utils/error-parser';
import { isSpotSource, useSources } from './sources.api';
import { getDetails } from './spots.api';

export interface SearchMediaResult {
    hasMore: boolean;
    currentPage: number;
    location: PSLocation;
    results: PSMedia[];
}

interface UseOriginalMediaResult {
    originalMedia: CMSMediaOrCoverImage | null;
    error: string | null;
    isLoading: boolean;
}

/**
 * Retrieves the original media if it is possible and converts it to CMSMedia
 */
export const useOriginalMedia = (
    media: CMSMediaOrCoverImage | null | undefined,
    spot?: PSSpot_v15_Minimal,
): UseOriginalMediaResult => {
    const [
        originalMedia,
        setOriginalMedia,
    ] = useState<CMSMediaOrCoverImage | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { sources } = useSources();
    const [
        propMediaMemoized,
        setPropMediaMemoized,
    ] = useState<CMSMediaOrCoverImage | null>(null);

    // We make sure to not run everything many times just because media object reference has changed
    useEffect(() => {
        setPropMediaMemoized((prevState) => {
            if (JSON.stringify(prevState) !== JSON.stringify(media)) {
                return media ?? null;
            }
            return prevState;
        });
    }, [media]);

    const mediaSource = useMemo(
        () =>
            (sources || []).find((source) => {
                if (!media) {
                    return false;
                }
                if (isCoverImageSchema(media)) {
                    return source.name === media.source;
                }
                return source.id === media.source_id;
            }),
        [sources, media],
    );

    useEffect(() => {
        if (!propMediaMemoized || !mediaSource) {
            return;
        }

        getOriginalMediaFromSource(propMediaMemoized, mediaSource, spot)
            .then((newOriginalMedia) => setOriginalMedia(newOriginalMedia))
            .catch((error) => setError(parse(error)))
            .finally(() => setIsLoading(false));
    }, [mediaSource, propMediaMemoized, spot]);

    return { originalMedia, error, isLoading };
};

function getOriginalMediaFromSource(
    media: CMSMediaOrCoverImage,
    mediaSource: Source,
    spot?: PSSpot_v15_Minimal,
): Promise<CMSMediaOrCoverImage> {
    if (!media) {
        throw new Error('getOriginalMediaFromSource: no media');
    }
    if (!mediaSource) {
        throw new Error('getOriginalMediaFromSource: no mediaSource');
    }

    if (mediaSource.name === 'polarsteps' && isCMSMedia(media)) {
        return get<PSMedia>(
            `${BASE_URL}/cms/media/${media.media_id}?source_id=${media.source_id}&trip_id=${media.trip_id}`,
        ).then((data) => convertPSMediaToCMSMedia(data));
    }

    // We need to check if there is a spot because sometimes the media is coming from a spot that is not longer
    // assigned to that story
    if (spot && isSpotSource(mediaSource) && isCMSMedia(media)) {
        return getDetails(spot.id).then((spotDetails) => {
            // TODO: spot detail photos are paginated now, so we should get all those photos
            const originalPhoto = (spotDetails.photos?.data || [])
                .filter(isCMSMedia)
                .find(
                    (photo) =>
                        photo.external_id + '' === media.external_id + '',
                );
            if (originalPhoto) {
                return originalPhoto;
            }

            return media;
        });
    }

    return Promise.resolve(media);
}

/**
 * Searches media in Polarsteps related to a location
 */
const PAGE_SIZE = 1000;

export const searchFromLocation = (
    location: PSLocation,
    allowedMediaTypes: number[],
    page = 0,
): Promise<SearchMediaResult> => {
    const typePart = getMediaTypeParam(allowedMediaTypes);

    return get<PSMedia[]>(
        `${BASE_URL}/cms/media/search?lat=${location.lat}&lon=${location.lon}&limit=${PAGE_SIZE}${typePart}`,
    ).then((result = []) => {
        return {
            currentPage: page,
            // hasMore: result.length >= PAGE_SIZE,
            // Disabling pagination for now as offset was removed from the API due to performance issues - https://github.com/polarsteps/igloo/pull/6029
            // Currently we load the first 1000 and that's it
            hasMore: false,
            location,
            results: result,
        };
    });
};

function getMediaTypeParam(allowedMediaTypes: number[]): string {
    return allowedMediaTypes
        .map((mediaTypeId) => `&type=${mediaTypeId}`)
        .join('');
}

/**
 * Gets the user from a Media Object
 */
export const getUser = (media: Pick<PSMedia, 'step'>): Promise<PSUser> => {
    if (!media.step?.trip_id) {
        return Promise.reject(
            new Error(
                "Can't get user from media. No trip id resolved from step",
            ),
        );
    }

    return get<PSUser>(`${BASE_URL}/cms/trips/${media.step.trip_id}/user`);
};
